import React from 'react';
import { Modal, Form, Button, Alert } from 'react-bootstrap';
import { RxCross2 } from "react-icons/rx";
import { FaRegEye } from "react-icons/fa";
import { IoEyeOffOutline } from "react-icons/io5";
import { FaArrowLeftLong } from "react-icons/fa6";

export const CustomInput = ({ 
  label, 
  required = false, 
  error, 
  disabled = false,
  autoFocus = false,
  ...props 
}) => {
  return (
    <Form.Group className="mb-3">
      {label && (
        <Form.Label className="fw-semibold" style={{ color: '#000' }}>
          {label} {required && <span className="text-danger">*</span>}
        </Form.Label>
      )}
      <Form.Control
        {...props}
        className="py-2 custom-input"
        style={{ 
          color: '#000',
          borderColor: error ? '#dc3545' : '#000',
          transition: 'all 0.2s ease'
        }}
        disabled={disabled}
        autoFocus={autoFocus}
      />
      {error && (
        <div className="text-danger mt-1 small">
          {error}
        </div>
      )}
    </Form.Group>
  );
};

export const CustomPasswordInput = ({ 
  label = "Password", 
  required = false, 
  error, 
  disabled = false,
  showPassword,
  onTogglePassword,
  helpText,
  ...props 
}) => {
  return (
    <Form.Group className="mb-3">
      <Form.Label className="fw-semibold" style={{ color: '#000' }}>
        {label} {required && <span className="text-danger">*</span>}
      </Form.Label>
      <div className="position-relative">
        <Form.Control
          {...props}
          type={showPassword ? "text" : "password"}
          className="py-2 pe-5 custom-input"
          style={{ 
            color: '#000',
            borderColor: error ? '#dc3545' : '#000',
            transition: 'all 0.2s ease'
          }}
          disabled={disabled}
        />
        <button
          type="button"
          className="btn btn-link position-absolute top-50 end-0 translate-middle-y pe-3"
          onClick={onTogglePassword}
          style={{ 
            border: 'none', 
            background: 'none', 
            zIndex: 10,
            color: '#000'
          }}
          disabled={disabled}
        >
          {showPassword ? <IoEyeOffOutline size={18} /> : <FaRegEye size={18} />}
        </button>
      </div>
      {error && (
        <div className="text-danger mt-1 small">
          {error}
        </div>
      )}
      {helpText && (
        <Form.Text style={{ color: '#666' }}>
          {helpText}
        </Form.Text>
      )}
    </Form.Group>
  );
};

export const CustomSelect = ({ 
  label, 
  required = false, 
  error, 
  disabled = false,
  options = [],
  placeholder = "Select an option",
  ...props 
}) => {
  return (
    <Form.Group className="mb-3">
      {label && (
        <Form.Label className="fw-semibold" style={{ color: '#000' }}>
          {label} {required && <span className="text-danger">*</span>}
        </Form.Label>
      )}
      <Form.Select
        {...props}
        className="py-2 custom-input"
        style={{ 
          color: '#000',
          borderColor: error ? '#dc3545' : '#000',
          transition: 'all 0.2s ease'
        }}
        disabled={disabled}
      >
        <option value="" style={{ color: '#999' }}>{placeholder}</option>
        {options.map(option => (
          <option key={option.value} value={option.value} style={{ color: '#000' }}>
            {option.label}
          </option>
        ))}
      </Form.Select>
      {error && (
        <div className="text-danger mt-1 small">
          {error}
        </div>
      )}
    </Form.Group>
  );
};

export const CustomCheckbox = ({ 
  id,
  label, 
  disabled = false,
  ...props 
}) => {
  return (
    <Form.Group className="mb-4">
      <div className="d-flex align-items-start">
        <Form.Check
          {...props}
          type="checkbox"
          id={id}
          className="me-2 mt-1"
          disabled={disabled}
        />
        <Form.Label htmlFor={id} className="small" style={{ color: '#000' }}>
          {label}
        </Form.Label>
      </div>
    </Form.Group>
  );
};

export const CustomSubmitButton = ({ 
  children, 
  isLoading = false, 
  loadingText = "Loading...",
  disabled = false,
  ...props 
}) => {
  return (
    <div className="d-grid gap-2 mb-3">
      <Button
        {...props}
        type="submit"
        variant="primary"
        size="lg"
        disabled={isLoading || disabled}
        className="py-2"
      >
        {isLoading ? (
          <>
            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
            {loadingText}
          </>
        ) : (
          children
        )}
      </Button>
    </div>
  );
};

export const CustomAlert = ({ type = "danger", message, show = false }) => {
  if (!show || !message) return null;
  
  return (
    <Alert variant={type} className="mb-3">
      {message}
    </Alert>
  );
};

export const CustomModalHeader = ({ 
  title, 
  onClose, 
  showBackButton = false, 
  onBack 
}) => {
  return (
    <Modal.Header className="border-0 pb-0 position-relative">
      <button
        onClick={onClose}
        className="position-absolute top-0 end-0 mt-3 me-3 btn btn-link p-0"
        style={{ 
          border: 'none', 
          background: 'none',
          color: '#000',
          fontSize: '24px',
          lineHeight: '1',
          opacity: '0.7',
          transition: 'opacity 0.2s ease',
          zIndex: 10
        }}
        onMouseEnter={(e) => e.target.style.opacity = '1'}
        onMouseLeave={(e) => e.target.style.opacity = '0.7'}
      >
        <RxCross2 size={24} />
      </button>
      
      <Modal.Title className="w-100 text-center">
        <div className="d-flex align-items-center justify-content-center">
          {showBackButton && (
            <button
              type="button"
              className="btn btn-link p-0 me-3"
              onClick={onBack}
              style={{ 
                border: 'none', 
                background: 'none',
                color: '#000',
                opacity: '0.7',
                transition: 'opacity 0.2s ease'
              }}
              onMouseEnter={(e) => e.target.style.opacity = '1'}
              onMouseLeave={(e) => e.target.style.opacity = '0.7'}
            >
              <FaArrowLeftLong size={20} />
            </button>
          )}
          <h3 className="mb-0" style={{ color: '#000' }}>{title}</h3>
        </div>
      </Modal.Title>
    </Modal.Header>
  );
};

export const CustomLinkButton = ({ 
  children, 
  onClick, 
  disabled = false,
  className = "",
  ...props 
}) => {
  return (
    <button
      {...props}
      type="button"
      className={`btn btn-link p-0 text-decoration-none fw-semibold ${className}`}
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </button>
  );
};

export const CustomFormStyles = () => {
  return (
    <style jsx>{`
      .custom-input::placeholder {
        color: rgba(0, 0, 0, 0.3) !important;
        opacity: 1;
      }
      
      .custom-input:hover:not(:disabled) {
        border-color: #000 !important;
        box-shadow: 0 0 0 0.1rem rgba(0, 0, 0, 0.1) !important;
      }
      
      .custom-input:focus {
        border-color: #000 !important;
        box-shadow: 0 0 0 0.2rem rgba(0, 0, 0, 0.15) !important;
      }
      
      .custom-input:disabled {
        opacity: 0.6;
      }
      
      .form-select.custom-input option {
        color: #000 !important;
      }
      
      .form-select.custom-input option[value=""] {
        color: rgba(0, 0, 0, 0.3) !important;
      }
    `}</style>
  );
};